<template>
  <div class="survey-config-form">
    <div v-if="loading" class="text-center">
      <v-progress-circular :size="70" :width="7" color="primary" indeterminate></v-progress-circular>
    </div>
    <div v-else class="pa-4">
      <!-- Chip de configuración global -->
      <div v-if="isUsingGlobalConfig" class="mb-6">
        <v-chip color="#F3E5F5" text-color="#6E17A5" class="font-weight-bold">
          {{ $t('company-config.globalConfig.label') }}
        </v-chip>
      </div>

      <!-- Resumen de configuración -->
      <v-card class="mb-6" elevation="2">
        <v-card-text class="pa-6">
          <div class="d-flex align-center mb-6">
            <v-avatar color="#f3e8ff" size="54" class="mr-4">
              <v-icon color="#6E17A5" size="30">mdi-clipboard-list</v-icon>
            </v-avatar>
            <div>
              <h2 class="text-h5 font-weight-bold" style="color: #6E17A5">{{ $t('company-config.survey.summary.title') }}</h2>
              <p class="text--secondary mt-1">{{ $t('company-config.survey.summary.description') }}</p>
            </div>
          </div>
          <v-row>
            <v-col cols="12" md="2" class="d-flex align-center px-4">
              <v-avatar color="#f3e8ff" size="40" class="mr-3 flex-shrink-0">
                <v-icon color="#6E17A5" size="22">mdi-clock-outline</v-icon>
              </v-avatar>
              <div style="min-width: 150px;">
                <p class="text--secondary text-caption mb-0">{{ $t('company-config.survey.hourSend1.label') }}</p>
                <h3 class="text-h4 font-weight-bold" style="color: #6E17A5">{{ formatDisplayTime(summaryData.hourSend1) }}</h3>
              </div>
            </v-col>
            <v-col cols="12" md="2" class="d-flex align-center px-4">
              <v-avatar color="#f3e8ff" size="40" class="mr-3 flex-shrink-0">
                <v-icon color="#6E17A5" size="22">mdi-clock-outline</v-icon>
              </v-avatar>
              <div style="min-width: 150px;">
                <p class="text--secondary text-caption mb-0">{{ $t('company-config.survey.hourSend2.label') }}</p>
                <h3 class="text-h4 font-weight-bold" style="color: #6E17A5">{{ formatDisplayTime(summaryData.hourSend2) }}</h3>
              </div>
            </v-col>
            <v-col cols="12" md="2" class="d-flex align-center px-4">
              <v-avatar color="#e8f8ed" size="40" class="mr-3 flex-shrink-0">
                <v-icon color="#1b9c4a" size="22">mdi-timer-outline</v-icon>
              </v-avatar>
              <div style="min-width: 150px;">
                <p class="text--secondary text-caption mb-0">{{ $t('company-config.survey.windowMinutes.label') }}</p>
                <h3 class="text-h4 font-weight-bold" style="color: #1b9c4a">{{ summaryData.windowMinutes || '-' }}</h3>
              </div>
            </v-col>
            <v-col cols="12" md="2" class="d-flex align-center px-4">
              <v-avatar color="#e8f8ed" size="40" class="mr-3 flex-shrink-0">
                <v-icon color="#1b9c4a" size="22">mdi-timer-outline</v-icon>
              </v-avatar>
              <div style="min-width: 150px;">
                <p class="text--secondary text-caption mb-0">{{ $t('company-config.survey.windowMinutesTwo.label') }}</p>
                <h3 class="text-h4 font-weight-bold" style="color: #1b9c4a">{{ summaryData.windowMinutesTwo || '-' }}</h3>
              </div>
            </v-col>
            <v-col cols="12" md="2" class="d-flex align-center px-4">
              <v-avatar color="#fff3e6" size="40" class="mr-3 flex-shrink-0">
                <v-icon color="#ff8800" size="22">mdi-gift-outline</v-icon>
              </v-avatar>
              <div style="min-width: 150px;">
                <p class="text--secondary text-caption mb-0">{{ $t('company-config.survey.rewardActive.label') }}</p>
                <h3 class="text-h4 font-weight-bold" style="color: #ff8800">{{ summaryData.rewardActive ? $t('company-config.survey.rewardActive.yes') : $t('company-config.survey.rewardActive.no') }}</h3>
              </div>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>

      <v-form ref="surveyForm" lazy-validation>
        <v-row>
          <!-- Horarios -->
          <v-col cols="12" md="6">
            <v-card class="h-100" elevation="2">
              <v-card-text class="pa-6">
                <div class="d-flex align-center mb-6">
                  <v-avatar color="#f3e8ff" size="54" class="mr-4">
                    <v-icon color="#6E17A5" size="30">mdi-clock-outline</v-icon>
                  </v-avatar>
                  <div>
                    <h2 class="text-h5 font-weight-bold" style="color: #6E17A5">{{ $t('company-config.schedule.title') }}</h2>
                    <p class="text--secondary">{{ $t('company-config.schedule.description') }}</p>
                  </div>
                </div>
                <v-row>
                  <v-col cols="12" sm="6">
                    <div class="d-flex align-center mb-2">
                      <v-label class="font-weight-bold mb-0">{{ $t('company-config.survey.hourSend1.label') }}</v-label>
                      <v-tooltip right max-width="300">
                        <template v-slot:activator="{ on, attrs }">
                          <v-icon
                            v-bind="attrs"
                            v-on="on"
                            color="grey"
                            size="18"
                            class="ml-2"
                          >
                            mdi-information-outline
                          </v-icon>
                        </template>
                        <span>
                          <strong>{{ $t('company-config.schedule.firstHour.tooltip') }}</strong><br><br>
                          {{ $t('company-config.schedule.firstHour.tooltipText') }}<br><br>
                          <em>{{ $t('company-config.schedule.firstHour.example') }}</em>
                        </span>
                      </v-tooltip>
                    </div>
                    <v-text-field
                      v-model="formData.hourSend1"
                      type="time"
                      :rules="hourSend1Rules"
                      required
                      outlined
                      dense
                      prepend-icon="mdi-clock-outline"
                      ref="hourSend1Field"
                      @input="handleHourSend1Input"
                    ></v-text-field>
                    <p class="text-caption text--secondary mt-2">{{ $t('company-config.schedule.firstHour.hint') }}</p>
                  </v-col>
                  <v-col cols="12" sm="6">
                    <div class="d-flex align-center mb-2">
                      <v-label class="font-weight-bold mb-0">{{ $t('company-config.survey.hourSend2.label') }}</v-label>
                      <v-tooltip right max-width="300">
                        <template v-slot:activator="{ on, attrs }">
                          <v-icon
                            v-bind="attrs"
                            v-on="on"
                            color="grey"
                            size="18"
                            class="ml-2"
                          >
                            mdi-information-outline
                          </v-icon>
                        </template>
                        <span>
                          <strong>{{ $t('company-config.schedule.secondHour.tooltip') }}</strong><br><br>
                          {{ $t('company-config.schedule.secondHour.tooltipText') }}<br><br>
                          <em>{{ $t('company-config.schedule.secondHour.example') }}</em>
                        </span>
                      </v-tooltip>
                    </div>
                    <v-text-field
                      v-model="formData.hourSend2"
                      type="time"
                      :rules="hourSend2Rules"
                      required
                      outlined
                      dense
                      prepend-icon="mdi-clock-outline"
                      ref="hourSend2Field"
                      @input="handleHourSend2Input"
                    ></v-text-field>
                    <p class="text-caption text--secondary mt-2">{{ $t('company-config.schedule.secondHour.hint') }}</p>
                  </v-col>
                </v-row>
              </v-card-text>
            </v-card>
          </v-col>

          <!-- Recompensas -->
          <v-col cols="12" md="6">
            <v-card class="h-100" elevation="2">
              <v-card-text class="pa-6">
                <div class="d-flex align-center mb-6">
                  <v-avatar color="#fff3e6" size="54" class="mr-4">
                    <v-icon color="#ff8800" size="30">mdi-gift-outline</v-icon>
                  </v-avatar>
                  <div>
                    <h2 class="text-h5 font-weight-bold" style="color: #6E17A5">{{ $t('company-config.rewards.title') }}</h2>
                    <p class="text--secondary">{{ $t('company-config.rewards.description') }}</p>
                  </div>
                </div>
                <v-card outlined class="rounded-xl pa-4 d-flex justify-space-between align-center">
                  <div>
                    <h3 class="font-weight-bold text-h6">{{ $t('company-config.rewards.active.label') }}</h3>
                    <p class="text--secondary text-caption mt-1">{{ $t('company-config.rewards.active.description') }}</p>
                  </div>
                  <div class="d-flex align-center">
                    <v-tooltip right max-width="300">
                      <template v-slot:activator="{ on, attrs }">
                        <v-icon
                          v-bind="attrs"
                          v-on="on"
                          color="#ff8800"
                          size="20"
                          class="mr-4"
                        >
                          mdi-information-outline
                        </v-icon>
                      </template>
                      <span>
                        <strong>{{ $t('company-config.rewards.active.tooltip') }}</strong><br><br>
                        {{ $t('company-config.rewards.active.tooltipText') }}
                      </span>
                    </v-tooltip>
                    <v-switch
                      v-model="formData.rewardActive"
                      color="#6E17A5"
                      hide-details
                    ></v-switch>
                  </div>
                </v-card>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>

        <!-- Ventanas de participación -->
        <v-row>
          <v-col cols="12">
            <v-card elevation="2">
              <v-card-text class="pa-6">
                <div class="d-flex align-center mb-6">
                  <v-avatar color="#e8f8ed" size="54" class="mr-4">
                    <v-icon color="#1b9c4a" size="30">mdi-timer-outline</v-icon>
                  </v-avatar>
                  <div>
                    <h2 class="text-h5 font-weight-bold" style="color: #6E17A5">{{ $t('company-config.windows.title') }}</h2>
                    <p class="text--secondary">{{ $t('company-config.windows.description') }}</p>
                  </div>
                </div>
                <v-row>
                  <v-col cols="12" sm="6">
                    <div class="d-flex align-center mb-2">
                      <v-label class="font-weight-bold mb-0">{{ $t('company-config.survey.windowMinutes.label') }}</v-label>
                      <v-tooltip right max-width="300">
                        <template v-slot:activator="{ on, attrs }">
                          <v-icon
                            v-bind="attrs"
                            v-on="on"
                            color="grey"
                            size="18"
                            class="ml-2"
                          >
                            mdi-information-outline
                          </v-icon>
                        </template>
                        <span>
                          <strong>{{ $t('company-config.windows.first.tooltip') }}</strong><br><br>
                          {{ $t('company-config.windows.first.tooltipText') }}<br><br>
                          <em>{{ $t('company-config.windows.first.example') }}</em>
                        </span>
                      </v-tooltip>
                    </div>
                    <v-text-field
                      v-model="formData.windowMinutes"
                      type="number"
                      :rules="windowMinutesRules"
                      required
                      min="1"
                      outlined
                      dense
                      append-icon="mdi-clock-outline"
                      ref="windowMinutesField"
                      @input="handleWindowMinutesInput"
                    ></v-text-field>
                  </v-col>
                  <v-col cols="12" sm="6">
                    <div class="d-flex align-center mb-2">
                      <v-label class="font-weight-bold mb-0">{{ $t('company-config.survey.windowMinutesTwo.label') }}</v-label>
                      <v-tooltip right max-width="300">
                        <template v-slot:activator="{ on, attrs }">
                          <v-icon
                            v-bind="attrs"
                            v-on="on"
                            color="grey"
                            size="18"
                            class="ml-2"
                          >
                            mdi-information-outline
                          </v-icon>
                        </template>
                        <span>
                          <strong>{{ $t('company-config.windows.second.tooltip') }}</strong><br><br>
                          {{ $t('company-config.windows.second.tooltipText') }}<br><br>
                          <em>{{ $t('company-config.windows.second.example') }}</em>
                        </span>
                      </v-tooltip>
                    </div>
                    <v-text-field
                      v-model="formData.windowMinutesTwo"
                      type="number"
                      :rules="windowMinutesTwoRules"
                      required
                      min="1"
                      outlined
                      dense
                      append-icon="mdi-clock-outline"
                      ref="windowMinutesTwoField"
                      @input="handleWindowMinutesTwoInput"
                    ></v-text-field>
                  </v-col>
                </v-row>
                <v-alert type="info" dense text class="mt-4" color="#1b9c4a" border="left">
                  {{ $t('company-config.windows.alert') }}
                </v-alert>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>
      </v-form>

      <!-- Botón de Acción -->
      <v-tooltip left>
        <template v-slot:activator="{ attrs }">
          <v-btn
            fab
            fixed
            bottom
            right
            dark
            large
            color="primary"
            v-bind="attrs"
            :loading="loading"
            :disabled="loading"
            @click="submitForm"
          >
            <v-icon dark>mdi-content-save</v-icon>
          </v-btn>
        </template>
        <span>{{ $t('button.save.text') }}</span>
      </v-tooltip>
    </div>
  </div>
</template>

<script>
import { mapActions } from 'vuex';
import Swal from 'sweetalert2';
import i18n from '@/plugins/i18n';

export default {
  name: 'SurveyConfigForm',
  props: {
    companyId: {
      type: [String, Number],
      required: true
    },
    initialData: {
      type: Object,
      default: () => ({})
    },
    isUsingGlobalConfig: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      formData: {
        hourSend1: null,
        hourSend2: null,
        windowMinutes: null,
        windowMinutesTwo: null,
        rewardActive: false
      },
      originalFormData: {
        hourSend1: null,
        hourSend2: null,
        windowMinutes: null,
        windowMinutesTwo: null,
        rewardActive: false
      },
      summaryData: {
        hourSend1: null,
        hourSend2: null,
        windowMinutes: null,
        windowMinutesTwo: null,
        rewardActive: false
      },
      loading: false,
      windowOverlapError: false,
      isReverting: false,
      hourSend1Rules: [
        v => !!v || i18n.t('company-config.survey.hourSend1.required')
      ],
      hourSend2Rules: [
        v => !!v || i18n.t('company-config.survey.hourSend2.required')
      ],
      windowMinutesRules: [
        v => !!v || i18n.t('company-config.survey.windowMinutes.required'),
        v => v > 0 || i18n.t('company-config.survey.windowMinutes.minValue')
      ],
      windowMinutesTwoRules: [
        v => !!v || i18n.t('company-config.survey.windowMinutesTwo.required'),
        v => v > 0 || i18n.t('company-config.survey.windowMinutesTwo.minValue')
      ]
    };
  },
  watch: {
    initialData: {
      handler(newVal) {
        if (newVal) {
          this.copyDataFromInitial(newVal);
        }
      },
      immediate: true,
      deep: true
    }
  },
  methods: {
    ...mapActions('COMPANYCONFIG', ['ACTION_PUT_COMPANY_SURVEY_CONFIG']),
    copyDataFromInitial(newVal) {
      // Copiar valores al resumen
      this.summaryData = {
        hourSend1: newVal.hourSend1 || null,
        hourSend2: newVal.hourSend2 || null,
        windowMinutes: newVal.windowMinutes || null,
        windowMinutesTwo: newVal.windowMinutesTwo || null,
        rewardActive: newVal.rewardActive !== undefined ? newVal.rewardActive : false
      };

      this.formData = {
        hourSend1: newVal.hourSend1 || null,
        hourSend2: newVal.hourSend2 || null,
        windowMinutes: newVal.windowMinutes || null,
        windowMinutesTwo: newVal.windowMinutesTwo || null,
        rewardActive: newVal.rewardActive !== undefined ? newVal.rewardActive : false
      };

      // Guardar copia de los datos originales
      this.originalFormData = {
        hourSend1: newVal.hourSend1 || null,
        hourSend2: newVal.hourSend2 || null,
        windowMinutes: newVal.windowMinutes || null,
        windowMinutesTwo: newVal.windowMinutesTwo || null,
        rewardActive: newVal.rewardActive !== undefined ? newVal.rewardActive : false
      };

      // Convertir objetos de tiempo a strings HH:MM
      this.initializeTimeFields();
    },
    handleHourSend1Input() {
      this.validateHourOrder('hourSend1');
      this.validateWindowOverlap('hourSend1');
    },
    handleHourSend2Input() {
      this.validateHourOrder('hourSend2');
      this.validateWindowOverlap('hourSend2');
    },
    handleWindowMinutesInput() {
      this.validateWindowOverlap('windowMinutes');
    },
    handleWindowMinutesTwoInput() {
      this.validateWindowOverlap('windowMinutesTwo');
    },
    validateHourOrder(changedField) {
      if (!this.formData.hourSend1 || !this.formData.hourSend2) {
        return;
      }

      const timeToMinutes = (timeStr) => {
        const [hours, minutes] = timeStr.split(':').map(Number);
        return hours * 60 + minutes;
      };

      const hour1 = timeToMinutes(this.formData.hourSend1);
      const hour2 = timeToMinutes(this.formData.hourSend2);

      if (hour1 >= hour2) {
        // Cerrar cualquier dropdown abierto
        if (this.$refs.surveyForm) {
          this.$refs.surveyForm.$el.blur();
        }
        document.activeElement?.blur();

        // Restablecer el valor original del campo que cambió
        if (changedField && this.originalFormData[changedField] !== null) {
          this.isReverting = true;
          const fieldRef = this.$refs[changedField + 'Field'];
          if (fieldRef) {
            fieldRef.internalValue = this.originalFormData[changedField];
          }
          this.formData[changedField] = this.originalFormData[changedField];
          setTimeout(() => {
            this.isReverting = false;
          }, 100);
        }

        // Mostrar Swal después de cerrar los dropdowns
        this.$nextTick(() => {
          Swal.fire({
            icon: 'warning',
            title: i18n.t('company-config.windows.hourOrder.title'),
            text: i18n.t('company-config.windows.hourOrder'),
            confirmButtonColor: '#6E17A5'
          });
        });
      }
    },
    validateWindowOverlap(changedField) {
      if (!this.formData.hourSend1 || !this.formData.hourSend2 ||
          !this.formData.windowMinutes || !this.formData.windowMinutesTwo) {
        this.windowOverlapError = false;
        return;
      }

      const overlap = this.calculateOverlap();
      const unreasonableWindow = this.checkUnreasonableWindow();

      if (overlap || unreasonableWindow) {
        this.handleOverlapError(changedField);
      } else {
        this.windowOverlapError = false;
      }
    },
    calculateOverlap() {
      const timeToMinutes = (timeStr) => {
        const [hours, minutes] = timeStr.split(':').map(Number);
        return hours * 60 + minutes;
      };

      const start1 = timeToMinutes(this.formData.hourSend1);
      const end1 = start1 + parseInt(this.formData.windowMinutes);
      const start2 = timeToMinutes(this.formData.hourSend2);
      const end2 = start2 + parseInt(this.formData.windowMinutesTwo);

      let overlap = false;
      if (start1 < start2) {
        overlap = end1 > start2;
      } else if (start2 < start1) {
        overlap = end2 > start1;
      } else {
        overlap = true;
      }

      return overlap;
    },
    checkUnreasonableWindow() {
      const maxReasonableMinutes = 1440;
      let unreasonableWindow = false;
      if (parseInt(this.formData.windowMinutes) > maxReasonableMinutes) {
        unreasonableWindow = true;
      }
      if (parseInt(this.formData.windowMinutesTwo) > maxReasonableMinutes) {
        unreasonableWindow = true;
      }

      return unreasonableWindow;
    },
    handleOverlapError(changedField) {
      this.windowOverlapError = true;
      document.activeElement?.blur();

      this.revertFieldValue(changedField);

      this.$nextTick(() => {
        Swal.fire({
          icon: 'warning',
          title: i18n.t('company-config.windows.overlap.title'),
          text: i18n.t('company-config.windows.overlap.reverted'),
          confirmButtonColor: '#6E17A5'
        });
      });
    },
    revertFieldValue(changedField) {
      if (changedField && this.originalFormData[changedField] !== null) {
        this.isReverting = true;
        const fieldRef = this.$refs[changedField + 'Field'];
        if (fieldRef) {
          fieldRef.internalValue = this.originalFormData[changedField];
        }
        this.formData[changedField] = this.originalFormData[changedField];
        setTimeout(() => {
          this.isReverting = false;
        }, 100);
      }
    },
    validateForm() {
      const errors = {};

      if (!this.formData.hourSend1) {
        errors.hourSend1 = i18n.t('company-config.survey.hourSend1.required');
      }

      if (!this.formData.hourSend2) {
        errors.hourSend2 = i18n.t('company-config.survey.hourSend2.required');
      }

      if (!this.formData.windowMinutes) {
        errors.windowMinutes = i18n.t('company-config.survey.windowMinutes.required');
      } else if (this.formData.windowMinutes <= 0) {
        errors.windowMinutes = i18n.t('company-config.survey.windowMinutes.minValue');
      }

      if (!this.formData.windowMinutesTwo) {
        errors.windowMinutesTwo = i18n.t('company-config.survey.windowMinutesTwo.required');
      } else if (this.formData.windowMinutesTwo <= 0) {
        errors.windowMinutesTwo = i18n.t('company-config.survey.windowMinutesTwo.minValue');
      }

      if (this.windowOverlapError) {
        errors.windowOverlap = i18n.t('company-config.windows.overlap');
      }

      return errors;
    },
    async submitForm() {
      try {
        const result = await Swal.fire({
          title: i18n.t('company-config.survey.actionSave.confirm'),
          icon: 'question',
          showCancelButton: true,
          confirmButtonText: i18n.t('button.save.text'),
          cancelButtonText: i18n.t('button.cancel.text'),
          confirmButtonColor: '#6E17A5'
        });

        if (result.isConfirmed) {
          // Validar el formulario antes de enviarlo
          const validationErrors = this.validateForm();

          // Si hay errores de validación, mostrarlos en un único Swal y no enviar el formulario
          if (Object.keys(validationErrors).length > 0) {
            const errorMessages = Object.values(validationErrors).join('<br>');
            Swal.fire({
              icon: 'error',
              title: i18n.t('label.errorsValidation.plural.title'),
              html: '<div class="swal-subtitle"><b>' + i18n.t('label.errorsValidation.text') + ':</b></div><br>' + errorMessages,
              confirmButtonColor: '#6E17A5'
            });
            return;
          }

          this.loading = true;

          // Convertir strings HH:MM a formato esperado por el backend
          const payload = {
            hourSend1: this.formatTime(this.formData.hourSend1),
            hourSend2: this.formatTime(this.formData.hourSend2),
            windowMinutes: parseInt(this.formData.windowMinutes),
            windowMinutesTwo: parseInt(this.formData.windowMinutesTwo),
            rewardActive: this.formData.rewardActive
          };

          const save = await this.ACTION_PUT_COMPANY_SURVEY_CONFIG({
            companyId: this.companyId,
            data: payload
          });

          if (!save) {
            return Swal.fire({
              icon: 'error',
              title: i18n.t('label.invalid.title'),
              text: i18n.t('label.invalid.text'),
              confirmButtonColor: '#6E17A5'
            });
          }

          Swal.fire({
            icon: 'success',
            title: i18n.t('company-config.survey.actionSave.success'),
            confirmButtonColor: '#6E17A5'
          });

          // Emitir evento para recargar configuración
          this.$emit('config-saved');
        } else {
          Swal.fire({
            position: 'top-end',
            icon: 'info',
            title: i18n.t('label.delete.cancel'),
            showConfirmButton: false,
            timer: 2500
          });
        }
      } catch (error) {
        console.error(i18n.t('label.failService'));
        Swal.fire({
          icon: 'error',
          title: i18n.t('label.error.title'),
          text: i18n.t('label.error.text'),
          confirmButtonColor: '#6E17A5'
        });
      } finally {
        this.loading = false;
      }
    },
    formatTime(time) {
      if (!time) return null;
      if (typeof time === 'string') return time;
      const hours = time.hours ? time.hours.toString().padStart(2, '0') : '00';
      const minutes = time.minutes ? time.minutes.toString().padStart(2, '0') : '00';
      return `${hours}:${minutes}`;
    },
    formatDisplayTime(time) {
      if (!time) return '-';
      if (typeof time === 'string') return time;
      const hours = time.hours ? time.hours.toString().padStart(2, '0') : '00';
      const minutes = time.minutes ? time.minutes.toString().padStart(2, '0') : '00';
      return `${hours}:${minutes}`;
    },
    initializeTimeFields() {
      // Convertir objeto de tiempo a string HH:MM para v-text-field type="time"
      if (this.formData.hourSend1 && typeof this.formData.hourSend1 === 'object') {
        this.formData.hourSend1 = this.formatTime(this.formData.hourSend1);
      }
      if (this.formData.hourSend2 && typeof this.formData.hourSend2 === 'object') {
        this.formData.hourSend2 = this.formatTime(this.formData.hourSend2);
      }
    }
  }
};
</script>

<style scoped>
.info-badge {
  text-align: center;
}
</style>
